import { Component } from '@theme/component';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const mxPaginationEnglish = document.documentElement.lang.toLowerCase().startsWith('en');

export default class PaginatedList extends Component {
  pages = new Map();
  infinityScrollObserver;
  #requests = new Map();
  #rendering = new Set();
  #generation = 0;
  #failedNext = '';
  #aspectRatioHelper;
  #moreButton;
  #status;
  #frame;
  #scrollFrame;
  #controllers = new Set();
  #fallback;
  #stock = null;
  #knownGrid;
  #domObserver = new MutationObserver(() => {
    const grid = this.querySelector('[ref="grid"]');
    if (grid && (grid !== this.#knownGrid || !this.#moreButton?.isConnected)) this.#scheduleReset();
  });

  connectedCallback() {
    super.connectedCallback();
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    window.addEventListener('online', this.#retry);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#initialize();
    this.#domObserver.observe(this, { childList: true, subtree: true });
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#scheduleReset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.#domObserver.disconnect();
    this.infinityScrollObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    window.removeEventListener('online', this.#retry);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    cancelAnimationFrame(this.#scrollFrame);
    for (const controller of this.#controllers) controller.abort();
    this.#controllers.clear();
  }

  #initialize() {
    if (this.getAttribute('infinite-scroll') !== 'true') return;
    const url = new URL(window.location.href);
    this.#knownGrid = this.querySelector('[ref="grid"]');
    this.#stock = this.dataset.stockFirst === 'true' && this.refs.grid && !url.searchParams.has('filter.v.availability') && Number(url.searchParams.get('page') || 1) === 1
      ? { availability: '1', page: 1, initial: true, done: false } : null;
    this.classList.toggle('mx-stock-loading', !!this.#stock);
    this.#setupControls();
    this.#observeViewMore();
    if (this.#stock) this.#renderPage('next'); else this.#fetchPage('next');
    this.#updateControls();
    this.#checkNext();
  }

  #setupControls() {
    if (this.#moreButton?.isConnected) return;
    const controls = document.createElement('div');
    controls.className = 'mx-pagination-controls';
    controls.style.cssText = 'grid-column:1/-1;text-align:center;padding:24px 16px;';
    this.#moreButton = document.createElement('button');
    this.#moreButton.type = 'button';
    this.#moreButton.className = 'button button-secondary';
    this.#moreButton.style.cssText = 'min-height:44px;border-radius:30px;padding:12px 28px;';
    this.#moreButton.addEventListener('click', this.#retry);
    this.#status = document.createElement('p');
    this.#status.setAttribute('role', 'status');
    this.#status.setAttribute('aria-live', 'polite');
    this.#status.style.cssText = 'font-size:13px;margin:10px 0 0;';
    this.#fallback = document.createElement('a');
    this.#fallback.textContent = (mxPaginationEnglish ? 'Open the next page' : 'Sonraki sayfayı aç');
    this.#fallback.style.cssText = 'display:block;font-size:13px;text-decoration:underline;margin-top:12px;';
    this.#fallback.hidden = true;
    controls.append(this.#moreButton, this.#status, this.#fallback);
    this.append(controls);
  }

  #observeViewMore() {
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (entry.target === this.refs.viewMoreNext) this.#renderPage('next');
        if (entry.target === this.refs.viewMorePrevious && entry.boundingClientRect.top > -100) this.#renderPage('previous');
      }
    }, { rootMargin: '2200px 0px', threshold: 0 });
    for (const node of [this.refs.viewMorePrevious, this.refs.viewMoreNext]) {
      if (node) this.infinityScrollObserver.observe(node);
    }
  }

  #getPage(type) {
    if (this.#stock) {
      if (type !== 'next' || this.#stock.done) return;
      const url = new URL(window.location.href);
      url.searchParams.set('filter.v.availability', this.#stock.availability);
      url.searchParams.set('page', String(this.#stock.page));
      url.hash = '';
      return { page: 'stock:' + this.#stock.availability + ':' + this.#stock.page, url, availability: this.#stock.availability };
    }
    const cards = this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]');
    if (!cards?.length) return;
    const card = cards[type === 'previous' ? 0 : cards.length - 1];
    const page = Number(card.dataset.page) + (type === 'previous' ? -1 : 1);
    const lastPage = Number(this.refs.grid?.dataset.lastPage);
    if (!Number.isFinite(page) || page < 1 || page > lastPage) return;
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';
    return { page, url };
  }

  async #fetchPage(type) {
    const info = this.#getPage(type);
    if (!info) return null;
    if (this.pages.has(info.page)) return this.pages.get(info.page);
    const key = info.url.toString();
    if (this.#requests.has(key)) return this.#requests.get(key);
    const generation = this.#generation;
    const request = (async () => {
      try {
        let html;
        const sectionUrl = new URL(info.url);
        sectionUrl.searchParams.set('section_id', this.sectionId);
        for (let attempt = 0; attempt < 2; attempt++) {
          const controller = new AbortController();
          this.#controllers.add(controller);
          const timeout = setTimeout(() => controller.abort(), 6000);
          try {
            const response = await fetch(sectionUrl, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error('HTTP ' + response.status);
            html = await response.text();
            break;
          } catch (error) {
            if (attempt === 1 || generation !== this.#generation || !this.isConnected) throw error;
          } finally {
            clearTimeout(timeout);
            this.#controllers.delete(controller);
          }
        }
        if (!html) throw new Error('empty response');
        if (generation !== this.#generation || !this.isConnected) return null;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const cards = parsed.querySelectorAll('[ref="grid"] > [ref="cards[]"]');
        if (info.availability !== undefined) {
          if (!parsed.querySelector('results-list')) throw new Error('invalid collection response');
          if ([...cards].some(card => card.dataset.available !== (info.availability === '1' ? 'true' : 'false'))) throw new Error('availability filter unavailable');
        } else if (!cards.length) throw new Error('empty page');
        this.pages.set(info.page, html);
        if (type === 'next') this.#failedNext = ''; 
        return html;
      } catch (error) {
        if (generation === this.#generation && type === 'next') {
          this.#failedNext = key;
          if (this.#status) this.#status.textContent = (mxPaginationEnglish ? 'The connection is slow. Try again or open the next page.' : 'Bağlantı yavaşladı. Tekrar deneyebilir veya sonraki sayfayı açabilirsin.');
          this.#updateControls();
        }
        return null;
      } finally {
        if (this.#requests.get(key) === request) this.#requests.delete(key);
      }
    })();
    this.#requests.set(key, request);
    return request;
  }

  async #renderPage(type) {
    if (this.#rendering.has(type) || !this.isConnected) return;
    const info = this.#getPage(type);
    const grid = this.refs.grid;
    if (!info || !grid || (type === 'next' && this.#failedNext === info.url.toString())) return;
    const generation = this.#generation;
    this.#rendering.add(type);
    this.#updateControls();
    try {
      const html = await this.#fetchPage(type);
      if (!html || generation !== this.#generation || grid !== this.refs.grid || !this.isConnected) return;
      if (!this.#stock && grid.querySelector('[ref="cards[]"][data-page="' + info.page + '"]')) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const cards = [...parsed.querySelectorAll('[ref="grid"] > [ref="cards[]"]')];
      if (this.#stock) {
        const initial = this.#stock.initial;
        const existing = new Set(initial ? [] : [...grid.children].map(card => card.dataset.productId));
        const uniqueCards = cards.filter(card => !existing.has(card.dataset.productId));
        if (initial && cards.length) grid.replaceChildren(...uniqueCards);
        else if (!initial) grid.append(...uniqueCards);
        const lastPage = Number(parsed.querySelector('[ref="grid"]')?.dataset.lastPage || 0);
        if (this.#stock.page < lastPage) this.#stock.page++;
        else if (this.#stock.availability === '1') { this.#stock.availability = '0'; this.#stock.page = 1; }
        else this.#stock.done = true;
        if (cards.length || this.#stock.done) {
          if (initial && !cards.length) grid.replaceChildren();
          this.#stock.initial = false;
          this.classList.remove('mx-stock-loading');
          this.dataset.stockOrdered = 'true';
        }
        this.#aspectRatioHelper?.processNewElements();
        if (this.#status) this.#status.textContent = mxPaginationEnglish ? 'In-stock products first.' : 'Stoktaki ürünler önce gösteriliyor.';
        this.#fetchPage('next');
        if (this.#stock.initial) requestAnimationFrame(() => this.#renderPage('next'));
        return;
      }
      const first = grid.firstElementChild;
      const oldTop = first?.getBoundingClientRect().top || 0;
      if (type === 'previous') grid.prepend(...cards); else grid.append(...cards);
      this.#aspectRatioHelper?.processNewElements();
      if (type === 'previous' && first) {
        scrollTo({ top: getScrollTop() + first.getBoundingClientRect().top - oldTop, behavior: 'instant' });
      }
      if (this.#status) this.#status.textContent = cards.length + (mxPaginationEnglish ? ' more products loaded.' : ' ürün daha yüklendi.');
      this.#fetchPage(type);
    } finally {
      if (generation === this.#generation) {
        this.#rendering.delete(type);
        this.#updateControls();
        requestAnimationFrame(() => this.#checkNext());
      }
    }
  }

  #onScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#checkNext();
    });
  };

  #checkNext() {
    const target = this.refs.viewMoreNext;
    if (target && target.getBoundingClientRect().top < window.innerHeight + 2200) this.#renderPage('next');
  }

  #updateControls() {
    if (!this.#moreButton) return;
    const next = this.#getPage('next');
    this.#moreButton.hidden = !next;
    this.#moreButton.disabled = this.#rendering.has('next');
    this.#moreButton.textContent = this.#rendering.has('next') ? (mxPaginationEnglish ? 'Loading products…' : 'Ürünler yükleniyor…') : this.#failedNext ? (mxPaginationEnglish ? 'Try again' : 'Tekrar dene') : (mxPaginationEnglish ? 'Load more products' : 'Daha fazla ürün göster');
    if (this.#fallback) {
      this.#fallback.hidden = !next || !this.#failedNext;
      if (next) this.#fallback.href = next.url.toString();
    }
    this.refs.grid?.setAttribute('aria-busy', String(this.#rendering.size > 0));
  }

  #retry = () => {
    this.#failedNext = '';
    if (this.#status) this.#status.textContent = '';
    this.#renderPage('next');
  };

  #scheduleReset() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#generation++;
      for (const controller of this.#controllers) controller.abort();
      this.#controllers.clear();
      this.pages.clear();
      this.#requests.clear();
      this.#rendering.clear();
      this.#failedNext = '';
      delete this.dataset.stockOrdered;
      if (this.#status) this.#status.textContent = '';
      this.#initialize();
    });
  }

  #handleFilterUpdate = event => {
    const section = event.target?.closest?.('[id^="shopify-section-"]');
    if (section && section.id !== 'shopify-section-' + this.sectionId) return;
    this.#scheduleReset();
  };

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }
}
